
import { useState, useEffect } from 'react';
import { billingService, Estimate, Invoice, Receipt, Contract } from '@/services/billing/billingService';
import { useToast } from '@/hooks/use-toast';

export const useBilling = () => {
  const { toast } = useToast();
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [estimatesData, invoicesData, receiptsData, contractsData] = await Promise.all([
        billingService.getEstimates(),
        billingService.getInvoices(),
        billingService.getReceipts(),
        billingService.getContracts()
      ]);

      setEstimates(estimatesData);
      setInvoices(invoicesData);
      setReceipts(receiptsData);
      setContracts(contractsData);
    } catch (error) {
      console.error('Error loading billing data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load billing data',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const signContract = async (estimateId: string, signatureData: any) => {
    try {
      await billingService.signContract(estimateId, signatureData);
      await loadData();
      toast({
        title: 'Contract Signed',
        description: 'Your contract has been signed successfully'
      });
    } catch (error) {
      console.error('Error signing contract:', error);
      toast({
        title: 'Error',
        description: 'Failed to sign contract',
        variant: 'destructive'
      });
    }
  };

  const createInvoice = async (estimateId: string, scheduledDate?: string) => {
    try {
      await billingService.createInvoiceFromEstimate(estimateId, scheduledDate);
      await loadData();
      toast({
        title: 'Invoice Created',
        description: 'Invoice has been created from estimate'
      });
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to create invoice',
        variant: 'destructive'
      });
    }
  };

  const scheduleInvoice = async (invoiceId: string, scheduledDate: string) => {
    try {
      await billingService.updateInvoiceSchedule(invoiceId, scheduledDate);
      await loadData();
      toast({
        title: 'Invoice Scheduled',
        description: 'Invoice has been scheduled successfully'
      });
    } catch (error) {
      console.error('Error scheduling invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to schedule invoice',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return {
    estimates,
    invoices,
    receipts,
    contracts,
    isLoading,
    signContract,
    createInvoice,
    scheduleInvoice,
    refreshData: loadData
  };
};
